import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Tag, ListChecks, Gift, Crown, MessageCircle, ArrowRight, Loader2, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const VERTICAL_EMOJI = {
  beleza: "💇", saude: "🩺", pet: "🐾", educacao: "📚", assistencia: "🛠️",
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTemplatePreview() {
  const { vertical } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const qs = new URLSearchParams();
        if (params.get("utm_source")) qs.append("utm_source", params.get("utm_source"));
        if (params.get("utm_campaign")) qs.append("utm_campaign", params.get("utm_campaign"));
        const url = `${API_BASE}/public/template-preview/${vertical}${qs.toString() ? "?" + qs.toString() : ""}`;
        const { data } = await axios.get(url);
        if (!data?.found) setNotFound(true);
        else setData(data);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [vertical]);

  // Apply niche primary color to the page as CSS variable
  useEffect(() => {
    if (!data?.branding?.primary_color) return;
    document.documentElement.style.setProperty("--tenant-primary", data.branding.primary_color);
    document.title = `${data.name} — SUA PLATAFORMA`;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-8 text-center">
        <div>
          <div className="font-display text-2xl font-semibold mb-2">Modelo não encontrado</div>
          <p className="text-muted-foreground mb-6">O segmento &quot;{vertical}&quot; não está disponível.</p>
          <Button onClick={() => nav("/")} variant="outline">Voltar para a página inicial</Button>
        </div>
      </div>
    );
  }

  const primary = data.branding?.primary_color || "262 61% 52%";
  const emoji = VERTICAL_EMOJI[vertical] || "✨";

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header
        className="relative overflow-hidden border-b border-border"
        style={{ background: `linear-gradient(135deg, hsl(${primary} / 0.10), hsl(${primary} / 0.02))` }}
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-6 border"
            style={{ borderColor: `hsl(${primary} / 0.3)`, color: `hsl(${primary})` }}
          >
            <Sparkles className="w-3.5 h-3.5" /> Modelo pronto · SUA PLATAFORMA
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-16 h-16 rounded-2xl grid place-items-center text-3xl text-white shrink-0"
              style={{ background: `hsl(${primary})` }}
            >
              {emoji}
            </div>
            <div className="min-w-0">
              <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                {data.name}
              </h1>
              <p className="text-muted-foreground mt-2 text-base lg:text-lg max-w-2xl">{data.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            <Badge variant="outline">{data.modules.length} recursos</Badge>
            <Badge variant="outline">{data.loyalty_rules.length} regras de fidelidade</Badge>
            <Badge variant="outline">{data.tiers.length} níveis</Badge>
            <Badge variant="outline">{data.message_templates.length} modelos de mensagem</Badge>
            <Badge variant="outline">{data.custom_fields.length} campos extras</Badge>
          </div>
          <Button
            onClick={() => nav("/")}
            className="gap-2 h-12 px-6"
            style={{ background: `hsl(${primary})`, color: "white" }}
            data-testid="preview-cta-contact"
          >
            Quero esse pacote para minha empresa <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Sections */}
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 space-y-12">
        <Section icon={Tag} title="Vocabulário do seu segmento" subtitle="Termos personalizados em todo o sistema">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Term k="Cliente" v={data.terminology.client_singular} />
            <Term k="Serviço" v={data.terminology.service_singular} />
            <Term k="Atendimento" v={data.terminology.appointment_singular} />
            <Term k="Profissional" v={data.terminology.resource_singular} />
          </div>
        </Section>

        {data.custom_fields.length > 0 && (
          <Section icon={ListChecks} title="Campos extras de cadastro" subtitle="Coletamos as informações certas do seu nicho">
            <div className="grid sm:grid-cols-2 gap-2">
              {data.custom_fields.map((f, i) => (
                <Card key={i} className="p-4 flex items-center gap-3 rounded-xl border-border">
                  <Check className="w-4 h-4" style={{ color: `hsl(${primary})` }} />
                  <div>
                    <div className="font-medium text-sm">{f.label}</div>
                    <div className="text-xs text-muted-foreground">{f.field_type}{f.required ? " · obrigatório" : ""}</div>
                  </div>
                </Card>
              ))}
            </div>
          </Section>
        )}

        <Section icon={Gift} title="Regras de fidelidade prontas" subtitle="Pontuação automática desde o primeiro dia">
          <div className="grid sm:grid-cols-3 gap-3">
            {data.loyalty_rules.map((r, i) => (
              <Card key={i} className="p-5 rounded-xl border-border">
                <div className="font-display font-semibold mb-1">{r.name}</div>
                <div className="text-xs text-muted-foreground mb-3">{r.description}</div>
                <div className="text-2xl font-display font-bold" style={{ color: `hsl(${primary})` }}>+{r.points} pts</div>
              </Card>
            ))}
          </div>
        </Section>

        <Section icon={Crown} title="Níveis de cliente" subtitle="Promova automaticamente quem é fiel à sua marca">
          <div className="grid sm:grid-cols-3 gap-3">
            {data.tiers.map((t, i) => (
              <Card key={i} className="p-5 rounded-xl border-border">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: `hsl(${t.color || primary})` }} />
                  <div className="font-display font-semibold">{t.name}</div>
                </div>
                <div className="text-xs text-muted-foreground mb-3">a partir de {t.min_points} pontos</div>
                <ul className="space-y-1 text-xs">
                  {(t.benefits || []).map((b, j) => (
                    <li key={j} className="flex items-start gap-1.5">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: `hsl(${primary})` }} />
                      {b}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </Section>

        <Section icon={MessageCircle} title="Modelos de mensagem prontos" subtitle="Para WhatsApp, e-mail e SMS — já com placeholders">
          <div className="space-y-3">
            {data.message_templates.map((m, i) => (
              <Card key={i} className="p-5 rounded-xl border-border">
                <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <span>{m.channel}</span>
                  <span>·</span>
                  <span className="font-medium normal-case text-foreground">{m.name}</span>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground italic">&ldquo;{m.body}&rdquo;</p>
              </Card>
            ))}
          </div>
        </Section>

        {/* Final CTA */}
        <section
          className="rounded-2xl p-8 md:p-12 text-center border"
          style={{ background: `hsl(${primary} / 0.08)`, borderColor: `hsl(${primary} / 0.2)` }}
        >
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-3">
            Pronto para crescer com <span style={{ color: `hsl(${primary})` }}>{data.name}</span>?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Todo esse pacote vem pré-configurado na sua empresa em menos de 1 minuto.
          </p>
          <Button
            onClick={() => nav("/")}
            className="gap-2 h-12 px-8"
            style={{ background: `hsl(${primary})`, color: "white" }}
            data-testid="preview-cta-bottom"
          >
            Falar com vendas <ArrowRight className="w-4 h-4" />
          </Button>
        </section>
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        SUA PLATAFORMA · Crescimento, Relacionamento e Fidelização
      </footer>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg grid place-items-center bg-muted">
          <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Term({ k, v }) {
  return (
    <Card className="p-4 rounded-xl border-border text-center">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{k}</div>
      <div className="font-display font-semibold text-lg">{v || "—"}</div>
    </Card>
  );
}
